import { useLocale } from '@tundr/i18n';
import { useModal } from '@tundr/modal';
import { useNavigationGuardStore } from '@tundr/routing';
import { defineStore } from 'pinia';
import { type Component, computed, shallowRef } from 'vue';
import type { SidePanelConfig } from '../components/SidePanel';

export interface SidePanelStoreProps {
  component: null | Component;
  props?: Record<string, unknown>;
  config?: SidePanelConfig;
  events?: {
    onClose?: () => void;
    onConfirm?: () => void;
    onCancel?: () => void;
  };
  labels?: {
    confirm?: string;
    cancel?: string;
    confirmTestId?: string;
    cancelTestId?: string;
  };
}

const initialState = {
  component: null,
  props: {},
  config: { blocking: false, firstSelectionOnLastItem: true },
};

export const useSidePanelStore = defineStore('side-panel', () => {
  const sidePanelState = shallowRef<SidePanelStoreProps>(initialState);

  const { openConfirmModal } = useModal();
  const navigationGuardStore = useNavigationGuardStore();
  const { t } = useLocale('common');
  const navigationIsBlocked = computed(() => navigationGuardStore.isBlocked);

  const openSidePanel = ({
    component,
    props = {},
    config = { blocking: false, firstSelectionOnLastItem: true },
    events = {},
    labels = {},
  }: SidePanelStoreProps) => {
    sidePanelState.value = {
      component,
      props,
      config,
      events,
      labels,
    };
  };

  const closeSidePanel = () => {
    if (!isSidePanelOpen.value) {
      return;
    }
    /**
     * Centralized logic to check if the navigation is blocked and show the confirm modal
     */
    if (navigationIsBlocked.value) {
      openConfirmModal({
        title: t('confirm_close.title'),
        message: t('confirm_close.description'),
        config: { blocking: true },
        labels: {
          confirm: t('actions.close'),
          cancel: t('actions.cancel'),
        },
        events: {
          onConfirm: () => {
            sidePanelState.value.events?.onClose?.();
            navigationGuardStore.setIsBlocked(false);
            sidePanelState.value = initialState;
          },
        },
      });
      return;
    }
    if (sidePanelState.value.events?.onClose) {
      sidePanelState.value.events.onClose();
    }
    sidePanelState.value = initialState;
  };

  const isSidePanelOpen = computed<boolean>(
    () => !!sidePanelState.value.component,
  );

  return { sidePanelState, openSidePanel, closeSidePanel, isSidePanelOpen };
});
